import { DataTableConfig } from '../data-table/data-table-config.interface';
import { ColumnConfig } from '../field-renderer/field-config.interface';
import { TableColumnsProp } from '../../models/tables.model';
import {
  GridColumnsStorageNames,
  pageType,
  popupButtons,
} from '../../utility/constants';
import { PageEvent } from '@angular/material/paginator';

const MESSAGE_POPUP_ACTIONS_KEY = 'actions';

export type MessagePopupGatewayMode =
  | typeof pageType.shipmentGatewayPiece
  | typeof pageType.shipmentGatewayPieceExactMatch
  | typeof pageType.shipmentGatewayMasterShipment
  | typeof pageType.shipmentGatewayPieceShortage
  | typeof pageType.shipmentBag
  | typeof pageType.shipmentGatewayMasterShipment
  | string;

export interface BuildMessagePopupGridConfigOptions<TRow> {
  columns: TableColumnsProp[];
  rowIdField: keyof TRow | string;
  entityName: string;
  storageKey: string;
  selection?: boolean;
  pagination?: boolean;
  gatewayMode?: MessagePopupGatewayMode;
  rowSelectionDisabledFn?: (row: TRow) => boolean;
  noSearch?: string;
  noMatch?: string;
  includeActionsColumn?: boolean;
  hyperlinkKeys?: string[];
}

const MESSAGE_POPUP_STORAGE_KEY_MAP: Record<string, string> = {
  [pageType.shipmentGatewayPiece]: GridColumnsStorageNames.shipmentGatewayPiece,
  [pageType.shipmentGatewayPieceExactMatch]:
    GridColumnsStorageNames.shipmentGatewayPiece,
  [pageType.shipmentGatewayPieceShortage]:
    GridColumnsStorageNames.shipmentGatewayPieceShortage,
  [pageType.shipmentGatewayMasterShipment]:
    GridColumnsStorageNames.shipmentGatewayMasterShipment,
  [pageType.shipmentBag]: GridColumnsStorageNames.shipmentBag,
  [pageType.scanShipmentBag]: GridColumnsStorageNames.shipmentBag,
  [pageType.manifestMultipleBagSelection]:
    GridColumnsStorageNames.manifestMultipleBagSelection,
  [pageType.arMultiShipmentSelection]:
    GridColumnsStorageNames.arMultiShipmentSelection,
  [pageType.associateUfoShipments]: GridColumnsStorageNames.associateUfoShipments,
  [pageType.apSftpFiles]: GridColumnsStorageNames.apSftpFiles,
  [pageType.linkedAccounts]: GridColumnsStorageNames.linkedAccounts,
  [pageType.numberRangesSetRange]: GridColumnsStorageNames.smNumberRanges,
  [pageType.apChargeCodeSetDetails]: GridColumnsStorageNames.apChargesCodeDetail,
  'accounts-linked-accounts': GridColumnsStorageNames.linkedAccounts,
  'ap-sftp-files': GridColumnsStorageNames.apSftpFiles,
};

export function resolveMessagePopupStorageKey(isFrom: string): string {
  return MESSAGE_POPUP_STORAGE_KEY_MAP[isFrom] ?? isFrom;
}

export function resolveMessagePopupRowIdField(isFrom: string): string {
  if (
    isFrom === pageType.associateUfoShipments ||
    isFrom === pageType.shipmentGatewayPieceShortage
  ) {
    return 'hawbId';
  }
  if (isFrom === pageType.apSftpFiles || isFrom === 'ap-sftp-files') {
    return 'fileName';
  }
  if (
    isFrom === pageType.shipmentBag ||
    isFrom === pageType.scanShipmentBag ||
    isFrom === pageType.manifestMultipleBagSelection ||
    isFrom === pageType.arMultiShipmentSelection
  ) {
    return 'id';
  }
  return 'hawbId';
}

export function buildMessagePopupGridConfig<TRow>(
  options: BuildMessagePopupGridConfigOptions<TRow>
): DataTableConfig<TRow> {
  const dataColumns: ColumnConfig<TRow>[] = options.columns
    .filter(col => col.key !== MESSAGE_POPUP_ACTIONS_KEY)
    .map(col => ({
      key: col.key,
      label: col.label,
      isSelected: col.isSelected ?? true,
      defaultView: col.defaultView ?? true,
      sortable: false,
      isHyperlink: options.hyperlinkKeys?.includes(col.key) ?? false,
      isActionButton: col.key === 'selectInvoiceAction',
      actionButtonLabel:
        col.key === 'selectInvoiceAction' ? 'Add charges to Invoice' : undefined,
    }));

  const columns: ColumnConfig<TRow>[] = [...dataColumns];
  if (options.includeActionsColumn ?? !!options.gatewayMode) {
    columns.push({
      key: MESSAGE_POPUP_ACTIONS_KEY,
      label: 'Actions',
      isSelected: true,
      defaultView: true,
      sortable: false,
      isRowActions: true,
      rowActionsFn: (row: TRow) =>
        getGatewayRowActions(options.gatewayMode, row as Record<string, unknown>),
      rowActionDisabledFn: (row: TRow, action: string) =>
        isGatewayRowActionDisabled(options.gatewayMode, row as Record<string, unknown>, action),
    });
  }

  return {
    rowIdField: options.rowIdField as keyof TRow,
    entityName: options.entityName,
    storageKey: options.storageKey,
    columns,
    features: {
      selection: options.selection ?? false,
      pagination: options.pagination ?? false,
      sorting: false,
      columnReorder: false,
      selectAllAcrossPages: false,
      columnCustomization: false,
    },
    messages: {
      noSearch: options.noSearch ?? 'No records found',
      noMatch: options.noMatch ?? 'No records found',
      allPageSelected: (entity, count) => `All ${count} ${entity} selected.`,
      selectAll: (entity, total) => `Select all ${total} ${entity}`,
    },
    rowSelectionDisabledFn: options.rowSelectionDisabledFn,
  };
}

function getGatewayRowActions(
  mode: MessagePopupGatewayMode | undefined,
  row: Record<string, unknown>
): readonly string[] {
  if (!mode) {
    return [];
  }
  if (row['isPlaceholderRow']) {
    return [popupButtons.create];
  }
  if (mode === pageType.shipmentGatewayPiece) {
    return [popupButtons.proceed, popupButtons.createLinkedShipments];
  }
  if (mode === pageType.shipmentGatewayPieceExactMatch) {
    return [popupButtons.gateway];
  }
  if (mode === pageType.shipmentGatewayPieceShortage) {
    return [];
  }
  if (mode === pageType.shipmentGatewayMasterShipment) {
    return [popupButtons.proceed];
  }
  if (
    mode === pageType.shipmentBag ||
    mode === pageType.scanShipmentBag ||
    mode === pageType.manifestMultipleBagSelection
  ) {
    return [popupButtons.Select];
  }
  return [popupButtons.proceed];
}

function isGatewayRowActionDisabled(
  mode: MessagePopupGatewayMode | undefined,
  row: Record<string, unknown>,
  action: string
): boolean {
  if (
    action === popupButtons.create &&
    row['isPlaceholderRow'] &&
    row['someCondition'] !== true
  ) {
    return true;
  }
  return false;
}

export function buildManifestAttachmentDataTableConfig(): DataTableConfig<{
  displayName: string;
  storedFileName: string;
}> {
  return {
    rowIdField: 'storedFileName',
    entityName: 'Attachments',
    storageKey: GridColumnsStorageNames.manifestPublishAttachments,
    columns: [
      {
        key: 'displayName',
        label: 'Attachment',
        isSelected: true,
        defaultView: true,
        sortable: false,
      },
      {
        key: 'attachmentActions',
        label: 'Actions',
        isSelected: true,
        defaultView: true,
        sortable: false,
        isActionButton: true,
        actionButtonLabel: 'Delete',
      },
    ],
    features: {
      selection: false,
      pagination: false,
      sorting: false,
      columnReorder: false,
      selectAllAcrossPages: false,
      columnCustomization: false,
    },
    messages: {
      noSearch: 'No attachments added',
      noMatch: 'No attachments added',
      allPageSelected: (entity, count) => `All ${count} ${entity} selected.`,
      selectAll: (entity, total) => `Select all ${total} ${entity}`,
    },
  };
}

export function updateZeroBasedPagingPayload(
  paging: { fromIndex: number; toIndex: number },
  event: PageEvent
): void {
  const pageSize = event.pageSize;
  const pageIndex = event.pageIndex;
  if (pageIndex === 0) {
    paging.fromIndex = 0;
    paging.toIndex = pageSize;
  } else {
    paging.fromIndex = pageIndex * pageSize + 1;
    paging.toIndex = paging.fromIndex + pageSize - 1;
  }
}
